import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  SxProps,
  Typography
} from '@mui/material';
import { Button } from 'system/atoms';
import React, { useState } from 'react';
import { onboardingElementIds } from 'services/onboarding/constants';
import { TrackingEventName } from 'services/tracking/constants';
import { useTracking } from 'services/tracking/hooks';
import { ScheduleLaunchModal } from './ScheduleLaunchModal';

const modalContent: SxProps = {
  padding: '24px',
  minWidth: 400
};

const descriptionText: SxProps = {
  marginTop: 2,
  marginBottom: 3,
  color: 'customGrey.dark'
};

const buttonsContainer: SxProps = {
  gap: 2,
  display: 'flex',
  alignItems: 'center'
};

const launchingContentSx: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: 1
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onLaunchCampaign: (
    launchScheduleDateTime: string | undefined
  ) => Promise<boolean>;
  /** When true, shows only Confirm/Cancel buttons (for launching scheduled campaigns) */
  confirmOnly?: boolean;
};

export const LaunchCampaignModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onLaunchCampaign,
  confirmOnly = false
}) => {
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const { track } = useTracking();

  const handleLaunchNow = async () => {
    track(TrackingEventName.CampaignsCreationLaunchSendNow);
    setIsLaunching(true);
    try {
      const success = await onLaunchCampaign(undefined);
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to launch campaign:', error);
    } finally {
      setIsLaunching(false);
    }
  };

  const handleScheduleClick = () => {
    track(TrackingEventName.CampaignsCreationLaunchSchedule);
    setIsScheduleModalOpen(true);
  };

  const handleScheduleConfirm = async (
    scheduledDateTime: string
  ): Promise<boolean> => {
    try {
      const success = await onLaunchCampaign(scheduledDateTime);
      if (success) {
        onClose();
        setIsScheduleModalOpen(false);
      }
      return success;
    } catch (error) {
      console.error('Failed to schedule campaign launch:', error);
      return false;
    }
  };

  const handleClose = () => {
    if (isLaunching) return;
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogContent sx={modalContent}>
          <Typography variant="h5" fontWeight="bold">
            Launch campaign?
          </Typography>

          <Typography sx={descriptionText}>
            Your campaign will be live and conversations started with its
            recipients.
          </Typography>

          <Box sx={buttonsContainer}>
            <Button
              variant="primary"
              id={onboardingElementIds.sendCampaignButton}
              onClick={handleLaunchNow}
              disabled={isLaunching}
            >
              {isLaunching ? (
                <Box sx={launchingContentSx}>
                  <CircularProgress size={16} color="inherit" />
                  Launching...
                </Box>
              ) : confirmOnly ? (
                'Confirm'
              ) : (
                'Now'
              )}
            </Button>
            {!confirmOnly && (
              <Button
                variant="secondary"
                onClick={handleScheduleClick}
                disabled={isLaunching}
              >
                Schedule
              </Button>
            )}
            <Button
              variant="tertiary"
              onClick={handleClose}
              disabled={isLaunching}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <ScheduleLaunchModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onConfirm={handleScheduleConfirm}
      />
    </>
  );
};
